import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
} from '@mui/material';
import { apiService } from '../services/api';
import { Employee, EmploymentStatus, EmployeeAccess } from '../types';
import { COUNTRIES, CURRENCIES } from '../data/constants';

interface Department {
  id: string;
  name: string;
  entity: {
    id: string;
    name: string;
  };
}

interface Position {
  id: string;
  title: string;
  entity: {
    id: string;
    name: string;
  };
  department?: {
    id: string;
    name: string;
  };
}

interface Manager {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  email: string;
}

interface EditEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onEmployeeUpdated: () => void;
  employee: Employee | null;
}

const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  open,
  onClose,
  onEmployeeUpdated,
  employee,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    personalEmail: '',
    phoneNumber: '',
    dateOfBirth: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    employmentStatus: EmploymentStatus.ACTIVE,
    access: '',
    startDate: '',
    endDate: '',
    departmentId: '',
    positionId: '',
    managerId: '',
    employeeNumber: '',
    country: '',
    workMode: '',
  });

  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');

  // Salary state
  const [salaryAmount, setSalaryAmount] = useState('');
  const [salaryCurrency, setSalaryCurrency] = useState('HKD');
  const [activeCompId, setActiveCompId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadFormData();
    }
  }, [open, employee]);

  const loadFormData = async () => {
    if (!employee) return;

    try {
      setDataLoading(true);
      setError('');

      // Load dropdown data + compensation
      const [departmentsData, positionsData, employeesData, compData] = await Promise.all([
        apiService.getDepartments(),
        apiService.getPositions(),
        apiService.getEmployees(),
        apiService.getEmployeeCompensation(employee.id).catch(() => []),
      ]);

      const activeComp = compData.find((c: any) => c.isActive);
      // Prefer direct salary on employee record; fall back to compensation package
      if (employee.monthlySalary) {
        setSalaryAmount(String(employee.monthlySalary));
        setSalaryCurrency(employee.salaryCurrency || 'HKD');
        setActiveCompId(activeComp?.id || null);
      } else if (activeComp) {
        setSalaryAmount(String(activeComp.amount));
        setSalaryCurrency(activeComp.currency || 'HKD');
        setActiveCompId(activeComp.id);
      } else {
        setSalaryAmount('');
        setSalaryCurrency('HKD');
        setActiveCompId(null);
      }

      setDepartments(departmentsData);
      setPositions(positionsData);
      // Filter employees to use as potential managers
      const managersData = employeesData.filter((emp: any) => 
        emp.employmentStatus === 'active' && emp.id !== employee?.id
      ).map((emp: any) => ({
        id: emp.id,
        firstName: emp.firstName,
        lastName: emp.lastName,
        nickname: emp.nickname,
        email: emp.email,
      }));
      setManagers(managersData);

      // Populate form with employee data
      setFormData({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        nickname: employee.nickname || '',
        email: employee.email || '',
        personalEmail: employee.personalEmail || '',
        phoneNumber: employee.phoneNumber || '',
        dateOfBirth: employee.dateOfBirth ? employee.dateOfBirth.split('T')[0] : '',
        address: employee.address || '',
        emergencyContactName: employee.emergencyContactName || '',
        emergencyContactPhone: employee.emergencyContactPhone || '',
        employmentStatus: employee.employmentStatus || EmploymentStatus.ACTIVE,
        access: employee.access || '',
        startDate: employee.startDate ? employee.startDate.split('T')[0] : '',
        endDate: employee.endDate ? employee.endDate.split('T')[0] : '',
        departmentId: employee.department?.id || '',
        positionId: employee.position?.id || '',
        managerId: employee.manager?.id || '',
        employeeNumber: employee.employeeNumber || '',
        country: employee.country || '',
        workMode: employee.workMode || '',
      });
    } catch (err) {
      console.error('Error loading form data:', err);
      setError('Failed to load form data');
    } finally {
      setDataLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!employee) return;

    try {
      setLoading(true);
      setError('');

      // Prepare update data - only send non-empty fields
      const updateData = Object.entries(formData).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      // Include salary in the employee record update
      if (salaryAmount && Number(salaryAmount) > 0) {
        updateData.monthlySalary = Number(salaryAmount);
        updateData.salaryCurrency = salaryCurrency || 'HKD';
      }

      await apiService.updateEmployee(employee.id, updateData);

      // Save salary if provided
      if (salaryAmount) {
        const compPayload = {
          compensationType: 'salary',
          currency: salaryCurrency,
          amount: Number(salaryAmount),
          effectiveFrom: new Date().toISOString().split('T')[0],
        };
        if (activeCompId) {
          await apiService.updateEmployeeCompensation(employee.id, activeCompId, compPayload);
        } else {
          await apiService.createEmployeeCompensation(employee.id, compPayload);
        }
      }

      onEmployeeUpdated();
      onClose();
    } catch (err: any) {
      console.error('Error updating employee:', err);
      setError(err.response?.data?.message || 'Failed to update employee');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      nickname: '',
      email: '',
      personalEmail: '',
      phoneNumber: '',
      dateOfBirth: '',
      address: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      employmentStatus: EmploymentStatus.ACTIVE,
      access: '',
      startDate: '',
      endDate: '',
      departmentId: '',
      positionId: '',
      managerId: '',
      employeeNumber: '',
      country: '',
      workMode: '',
    });
    setError('');
    setSalaryAmount('');
    setSalaryCurrency('HKD');
    setActiveCompId(null);
    onClose();
  };

  const isFormValid = formData.firstName && formData.lastName && formData.email;

  const filteredPositions = positions.filter(
    (position) => !formData.departmentId || position.department?.id === formData.departmentId
  );

  const availableManagers = managers.filter(
    (manager) => manager.id !== employee?.id // Don't allow employee to be their own manager
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Employee - {`${employee?.nickname || ''} ${employee?.lastName || ''}`.trim()}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dataLoading ? (
          <CircularProgress sx={{ display: 'block', margin: '2rem auto' }} />
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Personal Information */}
            <Grid size={12}>
              <h3>Personal Information</h3>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Nickname"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Work Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Personal Email"
                type="email"
                value={formData.personalEmail}
                onChange={(e) => handleInputChange('personalEmail', e.target.value)}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={COUNTRIES}
                getOptionLabel={(option) => option.name}
                value={COUNTRIES.find(c => c.name === formData.country) || null}
                onChange={(event, newValue) => {
                  handleInputChange('country', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country (Based In)"
                    helperText="Country where employee is based"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            
            <Grid size={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={formData.emergencyContactName}
                onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={formData.emergencyContactPhone}
                onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
              />
            </Grid>

            {/* Employment Information */}
            <Grid size={12}>
              <h3>Employment Information</h3>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Employee Number"
                value={formData.employeeNumber}
                onChange={(e) => handleInputChange('employeeNumber', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Employment Status</InputLabel>
                <Select
                  value={formData.employmentStatus}
                  onChange={(e) => handleInputChange('employmentStatus', e.target.value)}
                  label="Employment Status"
                >
                  <MenuItem value={EmploymentStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={EmploymentStatus.ON_LEAVE}>On Leave</MenuItem>
                  <MenuItem value={EmploymentStatus.TERMINATED}>Terminated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Work Mode</InputLabel>
                <Select
                  value={formData.workMode}
                  onChange={(e) => handleInputChange('workMode', e.target.value)}
                  label="Work Mode"
                >
                  <MenuItem value="">— Not set —</MenuItem>
                  <MenuItem value="onsite">Onsite</MenuItem>
                  <MenuItem value="remote">Remote</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.access === EmployeeAccess.APPROVER}
                      onChange={(e) => handleInputChange('access', e.target.checked ? EmployeeAccess.APPROVER : '')}
                    />
                  }
                  label="Approver"
                />
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={formData.employmentStatus === EmploymentStatus.ACTIVE}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <Autocomplete
                options={departments}
                getOptionLabel={(option) => `${option.name} (${option.entity.name})`}
                value={departments.find(d => d.id === formData.departmentId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('departmentId', newValue?.id || '');
                  if (!newValue || newValue.id !== formData.departmentId) {
                    handleInputChange('positionId', ''); // Clear position when department changes
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    helperText="Select department"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <Autocomplete
                options={filteredPositions}
                getOptionLabel={(option) => option.title}
                value={filteredPositions.find(p => p.id === formData.positionId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('positionId', newValue?.id || '');
                }}
                disabled={!formData.departmentId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Position"
                    helperText={formData.departmentId ? "Select position" : "Select department first"}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <Autocomplete
                options={availableManagers}
                loading={dataLoading}
                getOptionLabel={(option) => option ? `${option.nickname || ''} ${option.lastName || ''}`.trim() : ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={availableManagers.find(m => m.id === formData.managerId) || null}
                onChange={(_, newValue) => handleInputChange('managerId', newValue?.id || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manager"
                    placeholder="Search by nickname"
                  />
                )}
              />
            </Grid>

            <Grid size={12}><Divider sx={{ mt: 1 }} /></Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" fontWeight="bold">Monthly Salary</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={salaryCurrency}
                  onChange={(e) => setSalaryCurrency(e.target.value)}
                  label="Currency"
                >
                  {CURRENCIES.map((c: any) => (
                    <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Monthly Salary"
                type="number"
                value={salaryAmount}
                onChange={(e) => setSalaryAmount(e.target.value)}
                placeholder="e.g. 30000"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid || dataLoading}
        >
          {loading ? <CircularProgress size={20} /> : 'Update Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEmployeeModal;